import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/api';

const SCHOOL_ID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const HOME_BUTTON = '#00C8FF';

export function readSchoolId(searchParams) {
  const raw = String(
    searchParams.get('schoolId') ||
      searchParams.get('schoolID') ||
      searchParams.get('school_id') ||
      '',
  ).trim();
  return SCHOOL_ID_RE.test(raw) ? raw : '';
}

export function authHref(path, schoolId, extra) {
  const params = new URLSearchParams();
  if (schoolId) params.set('schoolId', schoolId);
  if (extra) {
    Object.keys(extra).forEach((key) => {
      const value = String(extra[key] || '').trim();
      if (value) params.set(key, value);
    });
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function shortSchoolName(name) {
  return String(name || '').replace(/\s+(university|college)$/i, '').trim();
}

export function possessiveSchool(name) {
  const base = shortSchoolName(name);
  if (!base) return 'your school';
  return /s$/i.test(base) ? `${base}'` : `${base}'s`;
}

// The page behind the card: the school's colour mixed almost all the way to white, so it reads
// as a tint rather than a wall of colour. A colour that is not a hex falls back to the default.
const HOME_BACKGROUND = '#e7f6ff';

export function lightTint(hex, amount = 0.12) {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return HOME_BACKGROUND;
  let digits = match[1];
  if (digits.length === 3) digits = digits.replace(/./g, (d) => d + d);
  const channel = (offset) => {
    const value = parseInt(digits.slice(offset, offset + 2), 16);
    const mixed = Math.round(255 - (255 - value) * amount);
    return mixed.toString(16).padStart(2, '0');
  };
  return `#${channel(0)}${channel(2)}${channel(4)}`;
}

function homeBrand(schoolId) {
  return {
    schoolId: schoolId || '',
    isSchool: false,
    ready: true,
    name: '',
    logoUrl: '',
    buttonColor: HOME_BUTTON,
    buttonTextColor: '#ffffff',
    backgroundColor: HOME_BACKGROUND,
  };
}

export function useSchoolBrand() {
  const [searchParams] = useSearchParams();
  const schoolId = readSchoolId(searchParams);
  const [brand, setBrand] = useState(() =>
    schoolId ? { ...homeBrand(''), schoolId, ready: false } : homeBrand(''),
  );

  useEffect(() => {
    if (!schoolId) {
      setBrand(homeBrand(''));
      return undefined;
    }

    let cancelled = false;
    setBrand((current) => ({ ...current, schoolId, ready: false }));

    apiService
      .getPublicSchoolBranding(schoolId)
      .then((config) => {
        if (cancelled) return;
        const name = String(config?.schoolName || '').trim();
        const logoUrl = String(
          config?.headerLogoUrl || config?.logoUrl || config?.schoolImageUrl || '',
        ).trim();
        const buttonColor = String(
          config?.loginButtonColor ||
            config?.buttonColor ||
            config?.primaryColor ||
            config?.inApp ||
            '#1565C0',
        ).trim();
        const buttonTextColor = String(config?.loginButtonTextColor || '#ffffff').trim();
        setBrand({
          schoolId,
          isSchool: true,
          ready: true,
          backgroundColor: lightTint(buttonColor),
          name: name || 'your school',
          logoUrl,
          buttonColor: buttonColor || '#1565C0',
          buttonTextColor: buttonTextColor || '#ffffff',
        });
      })
      .catch(() => {
        if (!cancelled) setBrand(homeBrand(schoolId));
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  return brand;
}
